import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { StatusChip, useMyRobots } from "@/entities/user-robot";
import { RobotAvatar } from "@/entities/robot";
import { ROUTES } from "@/shared/config";
import { formatMoney, formatPercent, moneySign, staggerIndex } from "@/shared/lib";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Список купленных роботов. Экрана в Figma нет: каркас взят у
 * `Robots · Catalog` (20:2), строка — у карточки `Active Robot` (19:99)
 * плюс `StatusChip/Robot` (DESIGN.md §5).
 *
 * Остановленные роботы из списка не пропадают: история по ним остаётся, и
 * пользователь должен видеть, во что превратились вложенные деньги.
 */
export function MyRobotsPage() {
  const { t } = useTranslation();
  const { data, isPending, isError, refetch } = useMyRobots();

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("myRobots.title")} subtitle={t("myRobots.subtitle")} />

      {isPending ? (
        <ScreenSkeleton blocks={[112, 112, 112]} />
      ) : isError ? (
        <ScreenState
          tone="loss"
          icon="warningFill"
          title={t("states.error.title")}
          body={t("states.error.body")}
          action={{ label: t("states.error.action"), onClick: () => void refetch() }}
        />
      ) : data.length === 0 ? (
        <ScreenState tone="info" icon="robotFill" title={t("myRobots.empty.title")} body={t("myRobots.empty.body")} />
      ) : (
        <ul className="flex flex-col gap-3">
          {data.map((robot, index) => {
            const sign = moneySign(robot.earned_total);
            const tone =
              sign < 0 ? "text-status-loss" : sign > 0 ? "text-status-profit" : "text-text-secondary";

            return (
              <li key={robot.id} className="stagger" style={staggerIndex(index)}>
                <Link
                  to={ROUTES.myRobot(robot.id)}
                  className="flex w-full flex-col gap-2.5 rounded-[20px] border border-border-subtle bg-background-elevated p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-[9px]">
                      <RobotAvatar robot={robot.robot} size={28} className="mt-0.5" />
                      <div className="flex flex-col gap-0.5">
                        <span className="text-body font-semibold text-text-primary">
                          {robot.robot.name}
                        </span>
                        <span className="text-caption font-normal text-text-secondary">
                          {t("myRobots.invested")} {formatMoney(robot.invested_total, { suffix: "USDT" })}
                        </span>
                      </div>
                    </div>
                    <StatusChip status={robot.status} />
                  </div>

                  <div className="flex items-baseline gap-2">
                    <span className={`text-body font-semibold ${tone}`}>
                      {formatMoney(robot.earned_total, { sign: "always", suffix: "USDT" })}
                    </span>
                    <span className={`text-label font-semibold ${tone}`}>
                      {formatPercent(robot.earned_pct, { sign: "always" })}
                    </span>
                  </div>

                  {/* Ожидающий капитал виден и в списке (ТЗ §19): иначе
                      пополнивший робота видит прежний «Вложено» и решает, что
                      деньги не дошли. Подпись короче, чем на экране робота, —
                      здесь она стоит в плотной строке, а не отдельным блоком. */}
                  {moneySign(robot.capital_pending) > 0 ? (
                    <span className="text-micro font-normal text-status-warning">
                      {t("myRobots.capital.pendingShort", {
                        amount: formatMoney(robot.capital_pending, { suffix: "USDT" }),
                      })}
                    </span>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
